import { useCallback, useEffect, useState } from 'react';
import styles from './AuthGate.module.css';
import { useAuthToken } from './useAuthToken';
import { useUser } from './useUser';
import { apiClient, removeStoredToken, setStoredToken } from '../../services/apiClient';
import { brandConfig } from '../../config/brands';
import { useAuthLogo, useAuthScheme } from '../../hooks/useAuthScheme';
import { applyAuthScheme, applyLoginBgImage, applyPartnerGa, resolveBgImageUrl } from '../../config/brands/applyBrandTheme';
import { GoogleSignInButton } from './GoogleSignInButton';
import type { GoogleSignInResult } from './googleSignIn';
import { appPath, hardNavigate, termsHref, isExternalTerms } from '../../utils/navigation';
import { postLoginTarget } from './postLoginRedirect';
import { renderAccentText } from '../../utils/renderAccentText';

type Props = {
  children: React.ReactNode;
};

interface ErrorResponse {
  error?: string;
  code?: string;
  email_resent?: boolean;
}

// 認証不要のパス（ランディングページ、認証ページなど）
const PUBLIC_PATHS = [
  '/lp/',
  '/auth/email/',
  '/auth/google/',
  '/auth/signup',
  '/accept-invitation/',
  '/decline-invitation/',
  '/onboarding',
  '/organizations/create',
  '/organizations/search',
  '/organizations/created',
  '/organizations/join-requested',
  '/organizations/invitation-accepted',
  '/org/',
  '/creators/', // ACP クリエイター公開プロフィール (未ログイン閲覧可)
  // 開発専用ハーネス (routes.tsx で import.meta.env.DEV のみ登録)。本番では 404 相当。
  ...(import.meta.env.DEV ? ['/__dev/'] : []),
];

function isPublicPath(): boolean {
  const path = window.location.pathname;
  // 公開ホーム対応ブランド (usodeki) はホームのみ未ログインで閲覧可。
  // - 完全一致のみ (前方一致にすると全パスが公開になる事故のため厳禁)
  // - ?token= 付き URL は除外: 公開扱いにすると children が AuthGate の
  //   トークン保存 useEffect より先にマウントし、ログイン済みなのに未ログイン
  //   表示になるため、従来のゲート経路 (保存 → loading 解除 → 描画) に落とす。
  // 注意: AuthGate は Router の外にいるため SPA 遷移 (<Link>) では再評価されない。
  // 公開ホームから保護ページへの導線は hardNavigate (フルリロード) を使うこと。
  if (brandConfig.features.publicHome) {
    const hasTokenParam = new URLSearchParams(window.location.search).has('token');
    const normalized = path.replace(/\/+$/, '') || '/';
    const home = appPath('/').replace(/\/+$/, '') || '/';
    if (!hasTokenParam && normalized === home) return true;
  }
  // VITE_APP_BASENAME=/app などサブパス配信時、pathname には basename が含まれる
  // (例: SAITE では "/app/auth/signup")。PUBLIC_PATHS は basename を含まない形で
  // 定義しているため、appPath() で basename を前置してから前方一致を判定する。
  return PUBLIC_PATHS.some(prefix => path.startsWith(appPath(prefix)));
}

const DEFAULT_LOGIN_TITLE = 'Welcome';
const DEFAULT_LOGIN_SUBTITLE = 'AIが、商品の魅力を引き出す';

const LOGIN_ACCENT_STYLE: React.CSSProperties = { color: 'var(--brand-accent-solid)' };

export function AuthGate({ children }: Props) {
  const { token, setToken } = useAuthToken();
  const { user } = useUser();
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [emailResent, setEmailResent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [loginTitle, setLoginTitle] = useState<string | null>(null);
  const [loginSubtitle, setLoginSubtitle] = useState<string | null>(null);

  // ライトモードでは、ダーク前提のログイン画面 (背景・カード・入力・テキスト) を
  // 生成り紙基調に上書きする。module 内の .light 配下セレクタで完結させ、
  // グローバル衝突を避ける。配色はパートナー設定 (data-auth-scheme) に動的追従し、
  // 未設定なら BrandConfig.colorScheme (usodeki 等) にフォールバックする。
  const isLight = useAuthScheme() === 'light';
  const logo = useAuthLogo();
  const wrapperClass = isLight ? `${styles.wrapper} ${styles.light}` : styles.wrapper;
  const loadingClass = isLight ? `${styles.loading} ${styles.light}` : styles.loading;

  useEffect(() => {
    const url = new URL(window.location.href);
    const newToken = url.searchParams.get('token');
    if (newToken) {
      setToken(newToken);
      url.searchParams.delete('token');
      window.history.replaceState({}, '', url.toString());
      setLoading(false);
      return;
    }
    setLoading(false);
  }, [setToken, token]);

  useEffect(() => {
    apiClient
      .get<{
        login_title: string | null;
        login_subtitle: string | null;
        login_bg_image_url: string | null;
        onboarding_color_scheme?: 'light' | 'dark' | null;
        ga4_measurement_id?: string | null;
      }>(`/partner/login-branding?partner_code=${brandConfig.id}`)
      .then(({ data }) => {
        if (data.login_title !== undefined) setLoginTitle(data.login_title);
        if (data.login_subtitle !== undefined) setLoginSubtitle(data.login_subtitle);

        // パートナー持ち込み GA4 の確定値。pre-auth キャッシュへ反映し、GTM へ
        // partner_ga_ready を通知する (null は未設定/計測停止としてキャッシュ失効)。
        applyPartnerGa(data.ga4_measurement_id ?? null);

        // オンボーディング画面 (auth-pages.css 系) の配色をパートナー設定で更新。
        // null は BrandConfig.colorScheme へのフォールバックとして扱われる。
        applyAuthScheme(data.onboarding_color_scheme ?? null);

        // 認証画面の背景画像。値ありは decode-first でフラッシュを避けてから CSS 変数 +
        // キャッシュを更新。null (未設定/解除) は変数とキャッシュを明示的に消す。
        const bgUrl = data.login_bg_image_url ?? null;
        if (bgUrl) {
          const resolved = resolveBgImageUrl(bgUrl);
          const swap = () => applyLoginBgImage(bgUrl, brandConfig.id);
          if (resolved) {
            const img = new Image();
            img.src = resolved;
            img.decode().then(swap).catch(swap);
          } else {
            swap();
          }
        } else {
          applyLoginBgImage(null, brandConfig.id);
        }
      })
      .catch(() => {/* ネットワークエラー時はキャッシュ値 or デフォルトで表示 */});
  }, []);

  const handleGoogleSuccess = useCallback((result: GoogleSignInResult) => {
    setStoredToken(result.token);
    if (result.redirect_to) {
      window.location.assign(appPath(result.redirect_to));
    } else if (result.is_new_user) {
      window.location.assign(appPath('/onboarding'));
    } else {
      // basename(/app) 外でのログインでも SPA へ確実に着地させる。
      // 復帰先はログイン画面が描画されている URL (= 開こうとしていたページ)。
      hardNavigate(postLoginTarget());
    }
  }, []);

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setErrorCode('');
    setSubmitting(true);

    try {
      const response = await apiClient.post('/auth/email/login', {
        email,
        password,
        origin: window.location.origin,
        partner_code: brandConfig.id,
      });

      const { token: newToken, is_new_user, redirect_to } = response.data;
      setStoredToken(newToken);

      if (is_new_user && redirect_to) {
        window.location.assign(appPath(redirect_to));
      } else {
        // basename(/app) 外 (例: saite.co.jp/) でログインすると reload では SPA が
        // 描画されず空白になるため、basename 込みのパスへ遷移する。
        // 復帰先はログイン画面が描画されている URL (= 開こうとしていたページ)。
        hardNavigate(postLoginTarget());
      }
    } catch (err: unknown) {
      const responseData = (err as { response?: { data?: ErrorResponse } })?.response?.data;
      setError(responseData?.error || 'ログインに失敗しました');
      setErrorCode(responseData?.code || '');
      setEmailResent(responseData?.email_resent || false);
    } finally {
      setSubmitting(false);
    }
  };

  // 認証不要のパスはそのまま表示
  if (isPublicPath()) {
    return <>{children}</>;
  }

  if (loading) {
    return <div className={loadingClass}>認証状態を確認しています...</div>;
  }

  if (!token) {
    return (
      <div className={wrapperClass}>
        <div className={styles.loginContainer}>
          {/* HypeApp 依頼2 (SAITE のみ設定): サービスサイト階層のパンくず */}
          {brandConfig.auth.loginBreadcrumb && (
            <nav className={styles.breadcrumb} aria-label="パンくずリスト">
              <ol>
                {brandConfig.auth.loginBreadcrumb.map((item) => (
                  <li key={item.label}>
                    {item.url ? (
                      <a href={item.url}>{item.label}</a>
                    ) : (
                      <span aria-current="page">{item.label}</span>
                    )}
                  </li>
                ))}
              </ol>
            </nav>
          )}
          <div className={styles.logoContainer}>
            {/* HypeApp 依頼3 (SAITE のみ設定): ロゴクリックでサービスサイトトップへ */}
            {brandConfig.auth.logoHref ? (
              <a href={brandConfig.auth.logoHref} className={styles.logoLink}>
                <img src={logo.src} alt={brandConfig.serviceName} className={styles.logo} />
              </a>
            ) : (
              <img src={logo.src} alt={brandConfig.serviceName} className={styles.logo} />
            )}
          </div>
          <div className={styles.card}>
            <h1>{renderAccentText(loginTitle ?? DEFAULT_LOGIN_TITLE, { style: LOGIN_ACCENT_STYLE })}</h1>
            <p>{loginSubtitle ?? DEFAULT_LOGIN_SUBTITLE}</p>
            <GoogleSignInButton
              partnerCode={brandConfig.id}
              intent="login"
              renderError={false}
              onSuccess={handleGoogleSuccess}
              onError={(err) => {
                setError(err.message || 'Google認証に失敗しました');
                setErrorCode((err as Error & { code?: string }).code || '');
              }}
            />
            {/* ACCOUNT_NOT_FOUND の案内は Google ログイン起点のため、divider (または) の上=
                Google ボタン直下に置いてボタンとの関係を分かりやすくする。
                「…見つかりませんでした。<a>新規登録</a>から…」の形で文中にリンクを埋め込む */}
            {error && errorCode === 'ACCOUNT_NOT_FOUND' && (
              <div className={styles.errorMessage}>
                <p style={{ margin: 0 }}>
                  {brandConfig.auth.notFoundGuidance.messagePrefix}
                  {brandConfig.auth.notFoundGuidance.type === 'internal' ? (
                    <a href={appPath(brandConfig.auth.notFoundGuidance.path)} className={styles.signupLink}>
                      {brandConfig.auth.notFoundGuidance.linkLabel}
                    </a>
                  ) : (
                    <a
                      href={brandConfig.auth.notFoundGuidance.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.signupLink}
                    >
                      {brandConfig.auth.notFoundGuidance.linkLabel}
                    </a>
                  )}
                  {brandConfig.auth.notFoundGuidance.messageSuffix}
                </p>
              </div>
            )}
            <div className={styles.divider}>
              <span>または</span>
            </div>

            {error && errorCode !== 'ACCOUNT_NOT_FOUND' && (
              (errorCode === 'EMAIL_NOT_CONFIRMED' && emailResent) ? (
                <div className={styles.successMessage}>
                  <p style={{ margin: '0 0 8px 0' }}>{error}</p>
                  <p style={{ margin: 0, fontSize: '13px' }}>
                    メールが届かない場合は、迷惑メールフォルダもご確認ください。
                  </p>
                </div>
              ) : errorCode === 'EMAIL_NOT_CONFIRMED' ? (
                <div className={styles.errorMessage}>
                  <p style={{ margin: '0 0 8px 0' }}>{error}</p>
                  <p style={{ margin: 0, fontSize: '13px' }}>
                    メールが届かない場合は、迷惑メールフォルダもご確認ください。
                  </p>
                </div>
              ) : (
                <div className={styles.errorMessage}>{error}</div>
              )
            )}

            <form className={styles.emailForm} onSubmit={handleEmailLogin}>
              <div className={styles.formGroup}>
                <input
                  type="email"
                  className={styles.input}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="メールアドレス"
                  required
                  autoComplete="email"
                />
              </div>
              <div className={styles.formGroup}>
                <input
                  type="password"
                  className={styles.input}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="パスワード"
                  required
                  autoComplete="current-password"
                />
              </div>
              <button type="submit" className={styles.emailLoginButton} disabled={submitting}>
                <svg className={styles.googleIcon} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="2"/>
                  <path d="M22 6L12 13L2 6"/>
                </svg>
                <span>{submitting ? 'ログイン中...' : 'メールアドレスでログイン'}</span>
              </button>
            </form>

            <div className={styles.emailLinks}>
              <a href={appPath('/auth/email/forgot-password')} className={styles.emailLink}>パスワードを忘れた場合</a>
            </div>

            {brandConfig.features.signupEnabled && (
              <div className={styles.tagline}>
                アカウントをお持ちでない方は<a href={appPath('/auth/signup')} className={styles.signupLink}>新規登録</a>
              </div>
            )}
          </div>
          <footer className={styles.footer}>
            <div className={styles.aboutLink}>
              <a href={brandConfig.urls.website} target="_blank" rel="noopener noreferrer">{brandConfig.serviceName}とは</a>
            </div>
            <div className={styles.footerLinks}>
              <a href={brandConfig.urls.privacy} target="_blank" rel="noopener noreferrer">プライバシーポリシー</a>
              <span className={styles.footerDivider}>|</span>
              <a href={termsHref()} {...(isExternalTerms() ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>利用規約</a>
              <span className={styles.footerDivider}>|</span>
              <a href={brandConfig.urls.contact} target="_blank" rel="noopener noreferrer">お問い合わせ</a>
            </div>
            <div className={styles.copyright}>
              © {brandConfig.companyName}
            </div>
          </footer>
        </div>
      </div>
    );
  }

  // 組織未確定 (onboarding 途中) のトークンでは認証済みアプリを描画しない。
  // ポイント残高など組織前提の API が全滅した「壊れたホーム」が表示されるため、
  // 初回登録 (組織作成) へ送り返す。トークンが失効していれば onboarding 側の
  // API 401 で除去され、通常のログイン画面に戻る。
  if (user && !user.organization_id) {
    window.location.replace(appPath('/onboarding'));
    return <div className={loadingClass}>初回登録ページへ移動しています...</div>;
  }

  return <AuthenticatedApp onSignOut={() => setToken(null)}>{children}</AuthenticatedApp>;
}

type AuthenticatedProps = {
  children: React.ReactNode;
  onSignOut: () => void;
};

function AuthenticatedApp({ children, onSignOut }: AuthenticatedProps) {
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      // セキュリティ: 同一オリジンからの postMessage のみ信用する。
      // (任意のフレーム/オープナーが攻撃者のトークンを注入してセッションを
      //  すり替えるのを防ぐ)
      if (event.origin !== window.location.origin) return;
      if (!event.data) return;
      if (typeof event.data === 'object' && 'token' in event.data) {
        const token = (event.data as { token?: string | null }).token ?? null;
        if (token) {
          setStoredToken(token || undefined);
        } else {
          removeStoredToken();
          onSignOut();
        }
      }
    };
    window.addEventListener('message', handler);
    const tokenExpiredHandler = (event: Event) => {
      const next = (event as CustomEvent<string | null>).detail ?? null;
      if (!next) {
        onSignOut();
      }
    };
    window.addEventListener('sj:token', tokenExpiredHandler as EventListener);
    return () => {
      window.removeEventListener('message', handler);
      window.removeEventListener('sj:token', tokenExpiredHandler as EventListener);
    };
  }, [onSignOut]);

  return <>{children}</>;
}

